import React, { useEffect, useMemo, useState } from "react";

import { PageWrapper } from "../../../../components/layout/container/Container";
import { SectionHeader } from "../../../../components/layout/header/Header";
import { Heading } from "../../../../components/common/heading/Heading";

import SecondaryPillTabs from "../../../../components/tabs/SecondaryPillTabs";

import StreamVideosGrid from "../../components/grids/StreamVideosGrid";

import CreatorModal from "../../../creator/components/modals/CreatorModal";
import CreatorStreamSetupForm from "./forms/CreatorStreamSetupForm";

import StreamLive from "../../components/play/StreamLive";
import StreamReplay from "../../components/play/StreamReplay";

import { useCreatorStreams } from "../../creators/hooks/useCreatorStreams";

import { formatRelativeTime } from "../../../../utils/datetime/formatRelativeTime";

import "./CreatorStreamsView.css";

const CreatorStreamsView = ({ username }) => {
  const [openBroadcastSetupModal, setOpenBroadcastSetupModal] = useState(false);

  const [openWatchModal, setOpenWatchModal] = useState(false);
  const [openReplayPlayer, setOpenReplayPlayer] = useState(false);

  const [streamSessionId, setStreamSessionId] = useState(null);
  const [selectedInfoStream, setSelectedInfoStream] = useState(null);

  const [streamFilter, setStreamFilter] = useState("all");

  const {
    streams,
    loading,
    error,

    getStreamsByUsername,
    resetCreatorStreams,
  } = useCreatorStreams(username);

  const streamList = useMemo(
    () => (Array.isArray(streams) ? streams : []),
    [streams],
  );

  useEffect(() => {
    if (!username) {
      return;
    }

    getStreamsByUsername();

    return () => {
      resetCreatorStreams();
    };
  }, [username]);

  const getStreamId = (stream) =>
    stream?.streamSessionId || stream?.stream_session_id || null;

  const getStatus = (stream) =>
    String(stream?.status || "")
      .trim()
      .toLowerCase();

  const getErrorMessage = () => {
    if (!error) return "";

    if (typeof error === "string") {
      return error;
    }

    return (
      error?.response?.data?.message ||
      error?.message ||
      "Something went wrong."
    );
  };

  const formatDateTime = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Melbourne",
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const getStreamImage = (stream) =>
    stream?.thumbnailUrl ||
    stream?.thumbnail_url ||
    stream?.thumbnail ||
    stream?.imageUrl ||
    stream?.image_url ||
    null;

  const getLivePlaybackUrl = (stream) =>
    stream?.ivsPlaybackUrl || stream?.ivs_playback_url || "";

  const getReplayPlaybackUrl = (stream) =>
    stream?.vodHlsUrl ||
    stream?.vod_hls_url ||
    stream?.vodMp4Url ||
    stream?.vod_mp4_url ||
    stream?.recordingPlaybackUrl ||
    stream?.recording_playback_url ||
    stream?.replayUrl ||
    stream?.replay_url ||
    "";

  const hasReplay = (stream) => Boolean(getReplayPlaybackUrl(stream));

  const isReplayStream = (stream) => {
    const status = getStatus(stream);

    return (
      status === "ready" ||
      (status === "ended" && hasReplay(stream)) ||
      hasReplay(stream)
    );
  };

  const canPlayStream = (stream) => {
    const status = getStatus(stream);

    if (status === "live") {
      return Boolean(getLivePlaybackUrl(stream));
    }

    return hasReplay(stream);
  };

  const getBadgeLabel = (stream) => {
    const status = getStatus(stream);

    if (status === "live") return "Live";
    if (status === "scheduled") return "Scheduled";
    if (isReplayStream(stream)) return "Replay";
    if (status === "ended") return "Ended";
    if (status === "recording_ready") return "Recording";
    if (status === "processing") return "Processing";
    if (status === "failed") return "Failed";

    return "Stream";
  };

  const getBadgeVariant = (stream) => {
    const status = getStatus(stream);

    if (status === "live") return "live";
    if (status === "scheduled") return "scheduled";
    if (isReplayStream(stream)) return "replay";
    if (status === "ended") return "ended";
    if (status === "recording_ready") return "recording";
    if (status === "processing") return "processing";
    if (status === "failed") return "failed";

    return "default";
  };

  const getReplayCreatedAt = (stream) =>
    stream?.createdAt ||
    stream?.created_at ||
    stream?.actualStart ||
    stream?.actual_start ||
    null;

  const getReplayDateText = (stream) => {
    const createdAt = getReplayCreatedAt(stream);

    if (!createdAt) {
      return "Replay available";
    }

    return formatRelativeTime(createdAt) || formatDateTime(createdAt);
  };

  const getStreamDetailText = (stream) => {
    const status = getStatus(stream);

    if (status === "live") {
      return "Streaming now";
    }

    if (status === "scheduled") {
      const scheduledStart = stream?.scheduledStart || stream?.scheduled_start;

      const scheduledText = formatDateTime(scheduledStart);

      return scheduledText ? `Scheduled ${scheduledText}` : "Scheduled";
    }

    if (isReplayStream(stream)) {
      return getReplayDateText(stream);
    }

    if (status === "ended") {
      return "Stream ended";
    }

    if (status === "recording_ready") {
      return "Recording ready";
    }

    if (status === "processing") {
      return "Processing replay";
    }

    if (status === "failed") {
      return "Stream failed";
    }

    return "Open stream";
  };

  const getInfoModalTitle = (stream) => {
    const status = getStatus(stream);

    if (status === "ended") {
      return "Stream ended";
    }

    if (status === "recording_ready") {
      return "Recording ready";
    }

    if (status === "processing") {
      return "Replay processing";
    }

    if (status === "failed") {
      return "Stream unavailable";
    }

    return stream?.title || "Stream information";
  };

  const getInfoModalMessage = (stream) => {
    const status = getStatus(stream);

    if (status === "ended") {
      return "This stream has ended and a replay is not available yet.";
    }

    if (status === "recording_ready") {
      return "The stream recording is ready to be linked and processed as a replay.";
    }

    if (status === "processing") {
      return "The stream has ended and the replay is currently being prepared.";
    }

    if (status === "failed") {
      return "This stream could not be loaded.";
    }

    if (status === "ready") {
      return "The replay record is ready, but its playback file is unavailable.";
    }

    return "This stream is not available yet.";
  };

  const getInfoModalMeta = (stream) => {
    const status = getStatus(stream);

    if (status === "scheduled") {
      return formatDateTime(stream?.scheduledStart || stream?.scheduled_start);
    }

    return formatDateTime(
      stream?.actualEnd ||
        stream?.actual_end ||
        stream?.createdAt ||
        stream?.created_at,
    );
  };

  const filteredStreams = useMemo(() => {
    if (streamFilter === "all") {
      return streamList;
    }

    if (streamFilter === "live") {
      return streamList.filter((stream) => getStatus(stream) === "live");
    }

    if (streamFilter === "scheduled") {
      return streamList.filter((stream) => getStatus(stream) === "scheduled");
    }

    if (streamFilter === "replay") {
      return streamList.filter(isReplayStream);
    }

    return streamList;
  }, [streamList, streamFilter]);

  const replayCount = useMemo(
    () => streamList.filter(isReplayStream).length,
    [streamList],
  );

  const streamFilterTabs = [
    {
      id: "all",
      label: "All",
      count: streamList.length,
    },
    {
      id: "live",
      label: "Live",
      count: streamList.filter((stream) => getStatus(stream) === "live").length,
    },
    {
      id: "scheduled",
      label: "Scheduled",
      count: streamList.filter((stream) => getStatus(stream) === "scheduled")
        .length,
    },
    {
      id: "replay",
      label: "Replay",
      count: replayCount,
    },
  ];

  const openLiveStream = (stream) => {
    const selectedStreamId = getStreamId(stream);

    if (!selectedStreamId) return;

    setStreamSessionId(selectedStreamId);
    setOpenWatchModal(true);
  };

  const openReplay = (stream) => {
    const selectedStreamId = getStreamId(stream);

    if (!selectedStreamId) return;

    setStreamSessionId(selectedStreamId);
    setOpenReplayPlayer(true);
  };

  const openStreamInfo = (stream) => {
    setSelectedInfoStream(stream);
  };

  const handleCardClick = (stream) => {
    const status = getStatus(stream);

    if (status === "scheduled" || status === "live") {
      openLiveStream(stream);
      return;
    }

    if (hasReplay(stream)) {
      openReplay(stream);
      return;
    }

    openStreamInfo(stream);
  };

  const handleSettingsClick = (stream) => {
    const selectedStreamId = getStreamId(stream);

    if (!selectedStreamId) return;

    setStreamSessionId(selectedStreamId);
    setOpenBroadcastSetupModal(true);
  };

  const handleCloseWatchModal = () => {
    setOpenWatchModal(false);
    setStreamSessionId(null);
  };

  const handleCloseReplayPlayer = () => {
    setOpenReplayPlayer(false);
    setStreamSessionId(null);
  };

  const handleCloseBroadcastSetup = () => {
    setOpenBroadcastSetupModal(false);
    setStreamSessionId(null);
  };

  const handleCloseInfoModal = () => {
    setSelectedInfoStream(null);
  };

  if (loading && streamList.length === 0) {
    return (
      <PageWrapper>
        <div className="creator-stream-grid-header">
          <div>
            <h2>Created Streams</h2>
            <p>Loading streams...</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <SectionHeader styles={{ marginTop: "1.5rem" }}>
        <Heading
          title={`My Streams`}
          size="sm"
          subTitle={"Manage your streams."}
        />

        <div className="creator-stream-grid-header-actions">
          <button
            type="button"
            className="creator-stream-grid-secondary-btn"
            onClick={getStreamsByUsername}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </SectionHeader>

      <SecondaryPillTabs
        tabs={streamFilterTabs}
        activeTab={streamFilter}
        onChange={setStreamFilter}
      />

      <br />

      {error && (
        <div className="creator-stream-grid-error">
          <p>{getErrorMessage()}</p>
        </div>
      )}

      <StreamVideosGrid
        streams={filteredStreams}
        selectedInfoStream={selectedInfoStream}
        getStreamId={getStreamId}
        getStatus={getStatus}
        canPlayStream={canPlayStream}
        getStreamImage={getStreamImage}
        getStreamDetailText={getStreamDetailText}
        getBadgeLabel={getBadgeLabel}
        getBadgeVariant={getBadgeVariant}
        getInfoModalTitle={getInfoModalTitle}
        getInfoModalMessage={getInfoModalMessage}
        getInfoModalMeta={getInfoModalMeta}
        onCardClick={handleCardClick}
        onSettingsClick={handleSettingsClick}
        onCloseInfoModal={handleCloseInfoModal}
      />

      {!loading && streamList.length === 0 && (
        <div className="creator-stream-grid-empty">
          <h3>No streams created yet</h3>
          <p>Create a stream first, then it will appear here.</p>
        </div>
      )}

      {!loading && streamList.length > 0 && filteredStreams.length === 0 && (
        <div className="creator-stream-grid-empty">
          <h3>No {streamFilter} streams found</h3>
          <p>Try switching to another filter.</p>
        </div>
      )}

      {openBroadcastSetupModal && streamSessionId && (
        <CreatorModal
          isOpen
          onClose={handleCloseBroadcastSetup}
          size="lg"
          title="Broadcast Setup"
          description="Connect OBS or YoloBox, manage the stream status, and control its replay."
        >
          <CreatorStreamSetupForm
            streamSessionId={streamSessionId}
            onClose={handleCloseBroadcastSetup}
            onDeleted={getStreamsByUsername}
          />
        </CreatorModal>
      )}

      <StreamLive
        streamSessionId={streamSessionId}
        isOpen={openWatchModal}
        onClose={handleCloseWatchModal}
      />

      <StreamReplay
        streamSessionId={streamSessionId}
        isOpen={openReplayPlayer}
        onClose={handleCloseReplayPlayer}
      />
    </PageWrapper>
  );
};

export default CreatorStreamsView;
